import {
  getDocumentsAsync,
  useCreateDocumentMutation,
  useCreateDocumentVersionMutation,
  useGetDevice,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetUser,
} from "@hooks";
import {
  AssistantMultiStepFormAnswer,
  AssistantProcess,
  AssistantProcessStatus,
  Document,
  DocumentVersion,
} from "@models";
import { NoteAddOutlined } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { DeviceDocumentChangeAnswer, Template, TemplateConfig } from "@types";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FinishProcess } from "src/components/Assistant/FinishProcess";
import { ListCard } from "src/components/Assistant/ListCard";
import { LoadingNewDocs } from "src/components/Assistant/LoadingNewDocs";
import { NavigationButtons } from "src/components/Assistant/NavigationButtons";
import { useSaveAnswer } from "src/components/FormStep/utils/useSaveAnswer";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { useGetAssistantProcesses } from "src/hooks/queries/assistant-process";
import { useUpdateAssistantProcessMutation } from "src/hooks/queries/assistant-process-mutations";

type DocumentUpdateTarget = {
  templateKey: string;
  documentId?: string;
  versionId?: string;
  changes: DeviceDocumentChangeAnswer[];
};

export const DU_UpdateDocs = () => {
  const { data: user } = useGetUser();
  const { orgId = "", deviceId = "" } = useParams();
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId);
  const { data: device } = useGetDevice({ orgId, deviceId });
  const { data: assistantProcesses } = useGetAssistantProcesses({
    orgId,
    deviceId,
    status: AssistantProcessStatus.IN_PROGRESS,
  });
  const { mutateAsync: updateAssistantProcess } =
    useUpdateAssistantProcessMutation(orgId, deviceId);
  const { mutateAsync: createDocument } = useCreateDocumentMutation();

  const [assistantProcess, setAssistantProcess] =
    useState<AssistantProcess | null>(null);

  useEffect(() => {
    const process = assistantProcesses?.[0];
    if (!assistantProcess && process) {
      setAssistantProcess(process);
    }
  }, [assistantProcesses]);

  const createDocumentVersionMutation = useCreateDocumentVersionMutation(
    orgId,
    deviceId
  );
  const { saveAnswer } = useSaveAnswer(orgId, deviceId);

  const [isGenerating, setIsGenerating] = useState<
    "loading" | "success" | "error" | "idle"
  >("idle");

  if (!user || !documents || !device || !assistantProcess || !templatesConfig)
    return null;

  const filteredChangedTemplateKeys = assistantProcess.state.changes
    .filter(
      (change) =>
        JSON.stringify(change.previousAnswer) !==
        JSON.stringify(change.acceptedAnswer)
    )
    .map((change) => change.templateKey);

  const uniqueTemplateKeys = [...new Set(filteredChangedTemplateKeys)];

  const createDocumentInstance = (templateKey: string): Promise<Document> => {
    return createDocument({
      orgId,
      body: {
        deviceId,
        templateKey,
      },
    });
  };

  const createDocumentVersion = async (document: Document) => {
    return await createDocumentVersionMutation.mutateAsync({
      docId: document.id,
    });
  };

  const saveAnswers = async (
    changes: DeviceDocumentChangeAnswer[],
    templateConfig: Template,
    document: Document,
    documentVersion: DocumentVersion
  ) => {
    return await Promise.all(
      changes.map(async (change) => {
        const answerElementConfig = templateConfig.questions.find(
          (element) => element.id === change.dataKeyId
        );

        if (!change.acceptedAnswer || !answerElementConfig) {
          console.error("No accepted answer or answer element config found");
          return;
        }

        return saveAnswer(
          document.id,
          change.acceptedAnswer,
          answerElementConfig,
          documentVersion
        );
      })
    );
  };

  const getRefreshedOrNewDocument = (
    target: DocumentUpdateTarget,
    refreshedDocuments: Document[],
    createdDocumentsByTarget: Map<DocumentUpdateTarget, Document>
  ): Document => {
    if (!!target.documentId !== !!target.versionId) {
      throw new Error(`Incomplete record target for ${target.templateKey}`);
    }

    const newDocument = createdDocumentsByTarget.get(target);
    if (newDocument) {
      return newDocument;
    }

    if (target.documentId && target.versionId) {
      const document = refreshedDocuments.find(
        (candidate) => candidate.id === target.documentId
      );
      if (
        !document ||
        document.templateKey !== target.templateKey ||
        !document.versions.some((version) => version.id === target.versionId)
      ) {
        throw new Error(
          `Document ${target.documentId} and version ${target.versionId} are not available`
        );
      }
      return document;
    }

    const matchingDocuments = refreshedDocuments.filter(
      (document) => document.templateKey === target.templateKey
    );
    if (matchingDocuments.length === 1) {
      return matchingDocuments[0];
    }
    if (matchingDocuments.length > 1) {
      throw new Error(
        `Multiple records use template ${target.templateKey}; documentId and versionId are required`
      );
    }

    throw new Error(
      `Document not found for template key ${target.templateKey}`
    );
  };

  const handleGenerateDocs = async ({
    templates,
  }: {
    templates: TemplateConfig;
  }) => {
    setIsGenerating("loading");

    const answersByTarget = assistantProcess.state.changes
      .filter(
        (
          answer: AssistantMultiStepFormAnswer
        ): answer is DeviceDocumentChangeAnswer =>
          !!answer.acceptedAnswer &&
          JSON.stringify(answer.previousAnswer) !==
            JSON.stringify(answer.acceptedAnswer)
      )
      .reduce<Record<string, DeviceDocumentChangeAnswer[]>>((acc, change) => {
        if (!!change.documentId !== !!change.versionId) {
          throw new Error(
            `Incomplete record target for data key ${change.dataKeyId}`
          );
        }

        const key = change.documentId
          ? `${change.documentId}:${change.versionId}`
          : change.templateKey;
        acc[key] = [...(acc[key] ?? []), change];
        return acc;
      }, {});

    const updateTargets = Object.values(answersByTarget).map((changes) => ({
      templateKey: changes[0].templateKey,
      documentId: changes[0].documentId,
      versionId: changes[0].versionId,
      changes,
    }));

    // We do it this weird way cause this causes cancellation errors https://github.com/TanStack/query/issues/8060 otherwise and the suggestions in the GH issue do not work
    // First we create the document instances if needed (either RCD or document does not exist)
    const documentsToCreate = updateTargets.filter(
      (target) =>
        templates[target.templateKey].regulatoryType === "RCD" ||
        (!target.documentId &&
          !documents.find((doc) => doc.templateKey === target.templateKey))
    );

    const createdDocumentsByTarget = new Map(
      await Promise.all(
        documentsToCreate.map(
          async (target): Promise<[DocumentUpdateTarget, Document]> => [
            target,
            await createDocumentInstance(target.templateKey),
          ]
        )
      )
    );

    // refetch the documents
    const refreshedDocuments = await getDocumentsAsync(orgId, deviceId);

    // now we create the new document versions
    const targetsWithDocumentVersions = await Promise.all(
      updateTargets.map(async (target) => {
        const document = getRefreshedOrNewDocument(
          target,
          refreshedDocuments,
          createdDocumentsByTarget
        );
        return {
          target,
          newDocumentVersion: await createDocumentVersion(document),
          document,
        };
      })
    );

    // Then for each version we save the answers
    await Promise.all(
      targetsWithDocumentVersions.map(
        async ({ target, newDocumentVersion, document }) => {
          await saveAnswers(
            target.changes,
            templates[target.templateKey],
            document,
            newDocumentVersion
          );
        }
      )
    );

    setIsGenerating("success");

    updateAssistantProcess({
      processId: assistantProcess.id,
      body: {
        status: AssistantProcessStatus.COMPLETED,
      },
    });
  };

  if (isGenerating === "loading") {
    return <LoadingNewDocs />;
  }

  if (isGenerating === "success") {
    return <FinishProcess />;
  }

  if (!templatesConfig) return null;

  return (
    <div className="flex flex-1 flex-col gap-y-4">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[
              { label: "Document Change Assistant" },
              { label: "Update Documents" },
            ]}
          />
        }
      />
      <div className="flex flex-col gap-y-3 py-2 px-3">
        <div className="flex flex-col">
          <Typography variant="h1" className="flex items-center gap-2">
            Please have all the below documents approved to complete your change
            management.
          </Typography>
        </div>
        <div className="flex flex-col gap-y-3 my-4">
          {uniqueTemplateKeys.map((templateKey) => {
            const template = templatesConfig[templateKey];

            return (
              <ListCard
                key={template.defaultTitle}
                text={template.defaultTitle}
                StartIcon={NoteAddOutlined}
                fullWidth
                justifyContent="flex-start"
                cursor="default"
              />
            );
          })}
        </div>
        <NavigationButtons
          rightButton={{
            text: "Generate Documents",
            onClick: () => handleGenerateDocs({ templates: templatesConfig }),
          }}
          leftButton={{
            hasConfirmationModal: true,
          }}
        />
      </div>
    </div>
  );
};
